import type { AxiosInstance } from 'axios';
import type { Logger } from '@wg/api-client';
import type {
  DealCode,
  DealCodeWithPresentation,
  DealWithMerchant,
  DealWithUserClaim,
  ClaimCodeResponse,
  DiscoverDealsRequest,
  DiscoverDealsResponse,
  LocationPoint,
  NearbyDealsRequest,
  NearbyDealsResponse,
  PresentationMethod,
  RecordPresentationResponse,
  TrackImpressionRequest,
  TrackImpressionResponse,
  UserClaimStatus,
  UserDealHistoryResponse,
  WeatherResponse,
} from '@wg/types';

export type { Logger };

export type DeviceType = 'ios' | 'android' | 'web';

export interface DealsApi {
  // Discovery
  discoverDeals(params: DiscoverDealsRequest): Promise<DiscoverDealsResponse>;
  getNearbyDeals(params: NearbyDealsRequest): Promise<NearbyDealsResponse>;
  getDealDetails(dealId: string): Promise<DealWithMerchant>;
  getDealWithUserClaim(dealId: string): Promise<DealWithUserClaim>;
  getMyDeals(options?: {
    category?: string;
    status?: UserClaimStatus;
    includeAvailable?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<DealWithUserClaim[]>;
  // Codes
  claimDealCode(dealId: string): Promise<ClaimCodeResponse>;
  getMyCodes(options?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<DealCode[]>;
  getMyWalletCodes(): Promise<DealCodeWithPresentation[]>;
  getMyDealHistory(options?: {
    limit?: number;
    offset?: number;
  }): Promise<UserDealHistoryResponse>;
  // Badge counts
  getNewDealsCount(): Promise<{ count: number; deal_ids: string[] }>;
  markDealsSeen(options: {
    deal_ids?: string[];
    all?: boolean;
  }): Promise<{ marked: number }>;
  // Tracking
  trackImpressions(
    params: TrackImpressionRequest,
  ): Promise<TrackImpressionResponse>;
  recordPresentation(
    codeId: string,
    method: PresentationMethod,
    options?: {
      location?: LocationPoint;
      locationId?: string;
      sessionId?: string;
      durationSeconds?: number;
    },
  ): Promise<RecordPresentationResponse>;
  // Weather
  getWeather(location: LocationPoint): Promise<WeatherResponse>;
}

const DEALS_BASE = '/deals/v1';

/**
 * Creates a deals API client. The `deviceType` is captured at construction
 * time so each platform (RN/web) can inject its own value without leaking
 * platform globals into shared code.
 */
export function createDealsApi(
  http: AxiosInstance,
  _logger: Logger,
  deviceType: DeviceType,
): DealsApi {
  return {
    async discoverDeals(params) {
      const response = await http.post<DiscoverDealsResponse>(
        `${DEALS_BASE}/discover`,
        params,
      );
      return response.data;
    },

    async getNearbyDeals(params) {
      const response = await http.post<NearbyDealsResponse>(
        `${DEALS_BASE}/nearby`,
        params,
      );
      return response.data;
    },

    async getDealDetails(dealId) {
      const response = await http.get<DealWithMerchant>(
        `${DEALS_BASE}/${dealId}`,
      );
      return response.data;
    },

    async getDealWithUserClaim(dealId) {
      const response = await http.get<DealWithUserClaim>(
        `${DEALS_BASE}/${dealId}/user-claim`,
      );
      return response.data;
    },

    async getMyDeals(options) {
      const response = await http.get<DealWithUserClaim[]>(
        `${DEALS_BASE}/me/deals`,
        {
          params: {
            category: options?.category,
            status: options?.status,
            include_available: options?.includeAvailable,
            limit: options?.limit,
            offset: options?.offset,
          },
        },
      );
      return response.data;
    },

    async claimDealCode(dealId) {
      const response = await http.post<ClaimCodeResponse>(
        `${DEALS_BASE}/claim`,
        { deal_id: dealId },
      );
      return response.data;
    },

    async getMyCodes(options) {
      const response = await http.get<DealCode[]>(`${DEALS_BASE}/me/codes`, {
        params: options,
      });
      return response.data;
    },

    async getMyWalletCodes() {
      const response = await http.get<DealCodeWithPresentation[]>(
        `${DEALS_BASE}/me/codes/wallet`,
      );
      return response.data;
    },

    async getMyDealHistory(options) {
      const response = await http.get<UserDealHistoryResponse>(
        `${DEALS_BASE}/me/history`,
        {
          params: options,
        },
      );
      return response.data;
    },

    async getNewDealsCount() {
      const response = await http.get<{ count: number; deal_ids: string[] }>(
        `${DEALS_BASE}/me/new-count`,
      );
      return response.data;
    },

    async markDealsSeen(options) {
      const response = await http.post<{ marked: number }>(
        `${DEALS_BASE}/me/mark-seen`,
        options,
      );
      return response.data;
    },

    async trackImpressions(params) {
      const response = await http.post<TrackImpressionResponse>(
        `${DEALS_BASE}/impressions`,
        params,
      );
      return response.data;
    },

    async recordPresentation(codeId, method, options) {
      const response = await http.post<RecordPresentationResponse>(
        `${DEALS_BASE}/present`,
        {
          code_id: codeId,
          method,
          location: options?.location,
          location_id: options?.locationId,
          device_type: deviceType,
          session_id: options?.sessionId,
          duration_seconds: options?.durationSeconds,
        },
      );
      return response.data;
    },

    async getWeather(location) {
      const response = await http.get<WeatherResponse>('/utils/v1/weather', {
        params: { lat: location.lat, lng: location.lng },
      });
      return response.data;
    },
  };
}

// ============================================================================
// Pure helpers (platform-agnostic)
// ============================================================================

export function formatDiscount(
  dealType: string,
  discountValue?: number,
): string {
  if (!discountValue) return '';

  switch (dealType) {
    case 'percentage':
      return `${discountValue}% OFF`;
    case 'fixed_amount':
      return `$${discountValue} OFF`;
    case 'bogo':
      return 'Buy 1 Get 1';
    case 'freebie':
      return 'FREE';
    case 'upgrade':
      return 'FREE Upgrade';
    default:
      return '';
  }
}

export function isExpiringSoon(expiresAt?: string): boolean {
  if (!expiresAt) return false;
  const expiry = new Date(expiresAt);
  const now = new Date();
  const hoursRemaining = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursRemaining > 0 && hoursRemaining <= 24;
}

export function getTimeRemaining(expiresAt?: string): string {
  if (!expiresAt) return '';

  const expiry = new Date(expiresAt);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) return 'Expired';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d remaining`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }

  return `${minutes}m remaining`;
}
